import React, { FC } from 'react';
import { Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';

import { EmailFormItem, Form, Select } from '~/components';
import { useGetApplication, useGetPayload } from '~/controllers/wizard';
import { useGetSignatories, useSendInvitationKsa, useSetSignatory } from '~/controllers/contract';
import emailValidation from '~/utils/emailValidation';
import { KsaAuthorizedSignatory } from '~/types/ksa/KsaAuthorizedSignatory';

interface Props {
  visible: boolean;
  onClose: () => void;
  handleGenerateClick?: any;
}

const SetAuthorizedSignatoryModal: FC<Props> = (props) => {
  const { visible, onClose, handleGenerateClick } = props;
  const { t } = useTranslation('ksaContract');
  const sendInvitationMutation = useSendInvitationKsa();
  const setSignatory = useSetSignatory();
  const { data: signatories, refetch } = useGetSignatories();
  const applicationQuery = useGetApplication();
  const payload = useGetPayload();
  const currentAuthorizedSignatory = payload?.authorizedSignatory as
    | KsaAuthorizedSignatory
    | undefined;

  const options = (signatories ?? [])
    .filter((item) => item.uuid !== currentAuthorizedSignatory?.uuid)
    .map((item) => ({
      label: `${item.fullName} (${item.email || 'no email'})`,
      value: item.uuid,
    }));

  const onFinish = (value: { uuid: string; email: string }) => {
    const newAuthorizedSignatoryState = signatories?.find(
      (item) => item.uuid === value.uuid
    )?.state;
    void setSignatory
      .mutateAsync(value.uuid)
      .then(() =>
        newAuthorizedSignatoryState === 'LINKED'
          ? Promise.resolve()
          : sendInvitationMutation.mutateAsync({ signatoryUuid: value.uuid, email: value.email })
      )
      .finally(() => {
        void refetch();
        onClose();
        form.resetFields();
        handleGenerateClick();
      });
  };

  const [form] = Form.useForm();

  const onCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      closable
      maskClosable={false}
      okButtonProps={{
        form: formName,
        htmlType: 'submit',
        loading: sendInvitationMutation.isLoading,
      }}
      cancelButtonProps={{ style: { display: 'none' } }}
      okText={t('Confirm', { ns: 'common' })}
      title={t('Who will sign the contract?')}
      centered
    >
      <Form form={form} name={formName} onFinish={onFinish}>
        <Form.Item
          rules={[{ required: true }]}
          name={['uuid']}
          label={t('Select authorised signatory')}
        >
          <Select options={options} loading={applicationQuery.isLoading} />
        </Form.Item>
        <Form.ShouldUpdate name={['uuid']}>
          {(formData) => {
            const uuid = get(formData.getFieldsValue(), 'uuid');
            const authorizedSignatory = signatories?.find((item) => item.uuid === uuid);
            if (authorizedSignatory?.state === 'LINKED' || !uuid) return null;
            const email = get(formData.getFieldsValue(), 'email');
            if (!email) {
              formData.setFields([{ name: 'email', value: authorizedSignatory?.email }]);
            }
            return (
              <>
                <EmailFormItem
                  rules={[{ required: true }, { validator: emailValidation }]}
                  name={['email']}
                />
                <Typography.Paragraph>
                  {t('We will send the invitation link to log in to the Portal.')}
                </Typography.Paragraph>
              </>
            );
          }}
        </Form.ShouldUpdate>
      </Form>
    </Modal>
  );
};

const formName = 'setAuthorizedRepresentative';
export default SetAuthorizedSignatoryModal;
